/**
 * Utilidades para el manejo de cursos y su ordenamiento cronológico
 */

// Orden cronológico correcto de los cursos educativos
export const COURSE_ORDER = [
  'Nivel medio mayor',
  'Prekínder', 
  'Kínder',
  'Primero básico',
  'Segundo básico',
  'Tercero básico',
  'Cuarto básico',
  'Quinto básico',
  'Sexto básico'
] as const;

/**
 * Obtiene el índice de orden cronológico de un curso
 * @param courseName - Nombre del curso
 * @returns Índice de orden (0-8) o 999 si no se encuentra
 */
export const getCourseOrderIndex = (courseName: string): number => {
  // Normalizar el nombre del curso para manejar variaciones
  const normalizedName = courseName.toLowerCase().trim();
  
  // Mapeo de variaciones de nombres a índices
  if (normalizedName.includes('nivel medio mayor') || normalizedName.includes('medio mayor')) {
    return 0; // Nivel medio mayor
  }
  if (normalizedName.includes('prekínder') || normalizedName.includes('prekinder') || normalizedName.includes('pre kinder')) {
    return 1; // Prekínder
  }
  if (normalizedName.includes('kínder') || normalizedName.includes('kinder')) {
    // Asegurarse de que no sea prekinder
    if (!normalizedName.includes('pre')) {
      return 2; // Kínder
    }
  }
  if (normalizedName.includes('primero básico') || normalizedName.includes('1°') || normalizedName.includes('1º')) {
    return 3; // Primero básico
  }
  if (normalizedName.includes('segundo básico') || normalizedName.includes('2°') || normalizedName.includes('2º')) {
    return 4; // Segundo básico
  }
  if (normalizedName.includes('tercero básico') || normalizedName.includes('3°') || normalizedName.includes('3º')) {
    return 5; // Tercero básico
  }
  if (normalizedName.includes('cuarto básico') || normalizedName.includes('4°') || normalizedName.includes('4º')) {
    return 6; // Cuarto básico
  }
  if (normalizedName.includes('quinto básico') || normalizedName.includes('5°') || normalizedName.includes('5º')) {
    return 7; // Quinto básico
  }
  if (normalizedName.includes('sexto básico') || normalizedName.includes('6°') || normalizedName.includes('6º')) {
    return 8; // Sexto básico
  }
  
  return 999; // No encontrado
};

/**
 * Obtiene el orden numérico detallado de un curso (para compatibilidad con VisualizacionResultados)
 * @param courseName - Nombre del curso
 * @returns Número de orden detallado
 */
export const getCourseDetailedOrder = (courseName: string): number => {
  // Extraer la parte base del curso (sin A/B/C)
  const courseBase = courseName.split(' ').slice(0, -1).join(' ');
  
  const orderMap: { [key: string]: number } = {
    // Medio Mayor (1-4)
    'Nivel medio mayor': 1,
    'Medio Mayor': 1,
    // Prekinder (5-7) 
    'Prekínder': 5,
    'Pre Kinder': 5,
    'Prekinder': 5,
    // Kinder (8-10)
    'Kínder': 8,
    'Kinder': 8,
    // Primero (11-13)
    'Primero básico': 11,
    // Segundo (14-16)
    'Segundo básico': 14,
    // Tercero (17-19)
    'Tercero básico': 17,
    // Cuarto (20-22)
    'Cuarto básico': 20,
    // Quinto (23-25)
    'Quinto básico': 23,
    // Sexto (26-28)
    'Sexto básico': 26
  };
  
  return orderMap[courseBase] || 100;
};

/**
 * Ordena una lista de cursos cronológicamente
 * @param courses - Lista de nombres de cursos
 * @returns Lista ordenada cronológicamente
 */
export const sortCoursesChronologically = (courses: string[]): string[] => {
  return courses.sort((a, b) => {
    const indexA = getCourseOrderIndex(a);
    const indexB = getCourseOrderIndex(b);
    if (indexA !== indexB) return indexA - indexB;
    return a.localeCompare(b); // Si son del mismo nivel, orden alfabético
  });
};

/**
 * Verifica si un curso es válido según el orden establecido
 * @param courseName - Nombre del curso
 * @returns true si el curso es válido
 */
export const isValidCourse = (courseName: string): boolean => {
  return getCourseOrderIndex(courseName) !== 999;
};

/**
 * Obtiene el nombre base de un curso (sin la letra de sección)
 * @param courseName - Nombre completo del curso
 * @returns Nombre base del curso
 */
export const getCourseBaseName = (courseName: string): string => {
  return courseName.split(' ').slice(0, -1).join(' ');
};

/**
 * Obtiene la sección de un curso (A, B, C, etc.)
 * @param courseName - Nombre completo del curso
 * @returns Letra de la sección o cadena vacía si no tiene
 */
export const getCourseSection = (courseName: string): string => {
  const parts = courseName.split(' ');
  const lastPart = parts[parts.length - 1];
  return lastPart.length === 1 && /[A-Z]/.test(lastPart) ? lastPart : '';
};